import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, XPBar, Coin, Zap, Flame } from "@/components/common";
import { RewardAdDialog } from "@/components/RewardAd";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CheckCircle2, Gift, Sparkles, Crown, Trash2, ScanLine, Trophy, Target, Ticket, Loader2, Megaphone, Users, MessageCircle } from "lucide-react";

export default function Home() {
  const { user, applyUser } = useAuth();
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [missions, setMissions] = useState([]);
  const [wheel, setWheel] = useState(null);
  const [ad, setAd] = useState(null); // {onComplete, label}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [b, a, m] = await Promise.all([
        api.get("/banner"), api.get("/announcements"), api.get("/missions"),
      ]);
      setBanner(b.data?.enabled ? b.data : null);
      setAnnouncements(a.data);
      setMissions(m.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const checkedInToday = user?.streak && missions.find((x) => x.id === "daily_checkin")?.complete;

  const checkin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rewards/checkin");
      applyUser(data.user);
      toast.success(`Day ${data.streak} streak! +${data.reward.coins} coins, +${data.reward.xp} XP`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const claimMission = async (id) => {
    try {
      const { data } = await api.post(`/missions/${id}/claim`);
      applyUser(data.user);
      toast.success(`Claimed! +${data.reward.coins} coins, +${data.reward.xp} XP`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const spinWheel = (viaAd) => {
    const run = async () => {
      try {
        const { data } = await api.post("/rewards/wheel", { via_ad: viaAd });
        applyUser(data.user);
        setWheel(data.prize);
        toast.success(`🎡 ${data.prize.label}!`);
      } catch (e) { toast.error(apiError(e)); }
    };
    if (viaAd) setAd({ label: "Extra Spin", onComplete: () => { setAd(null); run(); } });
    else run();
  };

  const openCrate = () => {
    setAd({ label: "Mystery Crate", onComplete: async () => {
      setAd(null);
      try {
        const { data } = await api.post("/rewards/ad", { placement: "mystery_crate" });
        applyUser(data.user);
        toast.success(`🎁 Crate: +${data.reward.coins} coins${data.reward.cash ? `, +$${data.reward.cash}` : ""}`);
      } catch (e) { toast.error(apiError(e)); }
    }});
  };

  if (!user) return null;

  return (
    <div className="space-y-5">
      {ad && <RewardAdDialog open onClose={() => setAd(null)} onComplete={ad.onComplete} label={ad.label} />}

      {/* Broadcast banner */}
      {banner && (
        <div className="rounded-2xl p-4 neon-green bg-gradient-to-r from-jixxeo-green/20 to-jixxeo-blue/10 border border-jixxeo-green/30" data-testid="home-banner">
          <div className="flex items-start gap-3">
            <Megaphone className="w-5 h-5 text-jixxeo-green mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="font-head font-extrabold text-white">{banner.title}</h3>
              <p className="text-sm text-white/70 mt-0.5">{banner.message}</p>
              {banner.cta_text && (
                <Button size="sm" onClick={() => navigate(banner.cta_link || "/scan")} data-testid="banner-cta"
                  className="mt-3 rounded-full bg-jixxeo-green text-black font-bold hover:bg-jixxeo-greenDark h-8">
                  {banner.cta_text}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Player card */}
      <div className="glass rounded-3xl p-5 border border-white/10">
        <div className="flex items-center gap-4">
          <Avatar user={user} size={64} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-head font-black text-xl truncate">{user.name}</h2>
              {user.elite && <span className="text-[10px] elite-shimmer text-white px-2 py-0.5 rounded-full font-bold">ELITE</span>}
            </div>
            <p className="text-xs text-jixxeo-blue font-bold">{user.rank}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 justify-end"><Coin /><span className="font-display text-jixxeo-gold text-lg">{user.coins}</span></div>
            <div className="flex items-center gap-1 justify-end text-jixxeo-danger text-sm"><Flame className="w-3.5 h-3.5" />{user.streak}d</div>
          </div>
        </div>
        <div className="mt-4"><XPBar progress={user.level_progress} /></div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="text-center"><p className="font-display text-lg text-jixxeo-green">{user.total_cleanups}</p><p className="text-[10px] uppercase text-muted-foreground">Cleanups</p></div>
          <div className="text-center"><p className="font-display text-lg text-white">{(user.total_weight_g/1000).toFixed(1)}kg</p><p className="text-[10px] uppercase text-muted-foreground">Collected</p></div>
          <div className="text-center"><p className="font-display text-lg text-jixxeo-blue">{user.badges?.length || 0}</p><p className="text-[10px] uppercase text-muted-foreground">Badges</p></div>
        </div>
      </div>

      {/* Scan CTA */}
      <button onClick={() => navigate("/scan")} data-testid="home-scan-cta"
        className="w-full rounded-3xl p-5 bg-gradient-to-r from-jixxeo-green to-jixxeo-greenDark text-black flex items-center justify-between neon-green hover:scale-[1.01] transition-transform">
        <div className="text-left">
          <p className="font-display text-xl">SCAN LITTER</p>
          <p className="text-sm font-medium opacity-80">Point your camera & let Jixxeo AI identify it</p>
        </div>
        <div className="w-14 h-14 rounded-full bg-black/15 flex items-center justify-center"><ScanLine className="w-8 h-8" /></div>
      </button>

      {/* Quick reward tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button onClick={checkin} disabled={checkedInToday || busy} data-testid="checkin-btn"
          className={`glass rounded-2xl p-4 flex flex-col items-center gap-2 hover:scale-[1.03] transition-transform ${checkedInToday ? "opacity-50" : "neon-gold"}`}>
          {busy ? <Loader2 className="w-7 h-7 animate-spin text-jixxeo-gold" /> : <CheckCircle2 className="w-7 h-7 text-jixxeo-gold" />}
          <span className="text-xs font-bold">{checkedInToday ? "Checked In" : "Daily Check-in"}</span>
        </button>
        <button onClick={openCrate} data-testid="mystery-crate-btn" className="glass rounded-2xl p-4 flex flex-col items-center gap-2 hover:scale-[1.03] transition-transform neon-blue">
          <Gift className="w-7 h-7 text-jixxeo-blue" /><span className="text-xs font-bold">Mystery Crate</span>
        </button>
        <button onClick={() => spinWheel(false)} data-testid="lucky-wheel-btn" className="glass rounded-2xl p-4 flex flex-col items-center gap-2 hover:scale-[1.03] transition-transform">
          <Ticket className="w-7 h-7 text-jixxeo-purple" /><span className="text-xs font-bold">Lucky Wheel</span>
        </button>
        <button onClick={() => navigate("/elite")} data-testid="elite-tile" className="glass rounded-2xl p-4 flex flex-col items-center gap-2 hover:scale-[1.03] transition-transform elite-shimmer">
          <Crown className="w-7 h-7 text-white" /><span className="text-xs font-bold text-white">Go Elite</span>
        </button>
      </div>

      {/* Community Feed CTA */}
      <button onClick={() => navigate("/community")} data-testid="home-community-cta"
        className="w-full rounded-3xl p-5 glass border border-jixxeo-blue/30 flex items-center justify-between hover:scale-[1.01] transition-transform neon-blue">
        <div className="flex items-center gap-3 text-left">
          <div className="w-12 h-12 rounded-full bg-jixxeo-blue/20 flex items-center justify-center"><Users className="w-6 h-6 text-jixxeo-blue" /></div>
          <div>
            <p className="font-head font-extrabold text-lg">Community Feed</p>
            <p className="text-sm text-muted-foreground">Share cleanup photos, like & comment</p>
          </div>
        </div>
        <MessageCircle className="w-6 h-6 text-jixxeo-blue" />
      </button>

      {/* Missions */}
      <div>
        <div className="flex items-center gap-2 mb-3"><Target className="w-5 h-5 text-jixxeo-green" /><h3 className="font-head font-extrabold text-lg">Missions</h3></div>
        <div className="space-y-2">
          {missions.map((m) => {
            const pct = Math.min(100, Math.round((m.progress / m.target) * 100));
            return (
              <div key={m.id} className="glass rounded-2xl p-4" data-testid={`mission-${m.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-bold text-sm flex items-center gap-2">{m.title}
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${m.type === "weekly" ? "bg-jixxeo-purple/20 text-jixxeo-purple" : "bg-jixxeo-blue/20 text-jixxeo-blue"}`}>{m.type}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">{m.desc}</p>
                  </div>
                  <div className="flex items-center gap-1 text-jixxeo-gold text-sm shrink-0"><Coin className="w-3.5 h-3.5" />{m.reward_coins}</div>
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <Progress value={pct} className="h-2 flex-1 bg-white/10" />
                  <span className="text-xs text-muted-foreground w-10 text-right">{m.progress}/{m.target}</span>
                  {m.complete && !m.claimed && (
                    <Button size="sm" onClick={() => claimMission(m.id)} data-testid={`claim-${m.id}`} className="h-7 rounded-full bg-jixxeo-green text-black font-bold text-xs hover:bg-jixxeo-greenDark">Claim</Button>
                  )}
                  {m.claimed && <span className="text-xs text-jixxeo-green font-bold">✓ Done</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Announcements */}
      {announcements.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-jixxeo-blue" /><h3 className="font-head font-extrabold text-lg">Announcements</h3></div>
          <div className="space-y-2">
            {announcements.map((a) => (
              <div key={a.id} className="glass rounded-2xl p-4" data-testid={`announcement-${a.id}`}>
                <p className="font-bold text-sm">{a.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {wheel && (
        <WheelResult prize={wheel} onClose={() => setWheel(null)} onSpinAgain={() => { setWheel(null); spinWheel(true); }} />
      )}
    </div>
  );
}

function WheelResult({ prize, onClose, onSpinAgain }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div className="glass rounded-3xl p-6 max-w-xs w-full text-center border border-jixxeo-purple/40 neon-purple" onClick={(e) => e.stopPropagation()}>
        <Trophy className="w-14 h-14 mx-auto text-jixxeo-gold pulse-glow" />
        <p className="font-display text-2xl mt-3 text-jixxeo-gold text-glow-gold">{prize.label}</p>
        <p className="text-sm text-muted-foreground mt-1">Lucky Wheel reward added!</p>
        <div className="flex gap-2 mt-5">
          <Button onClick={onClose} variant="ghost" className="flex-1 rounded-full border border-white/10">Close</Button>
          <Button onClick={onSpinAgain} data-testid="wheel-spin-again" className="flex-1 rounded-full bg-jixxeo-purple text-white font-bold hover:opacity-90">Ad Spin</Button>
        </div>
      </div>
    </div>
  );
}
